import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

const PALETTE = [
  '#e6ac42', '#d6de29', '#3c2ee7', '#625e7b', '#ac9bdd',
  '#f01a2f', '#feec0c', '#4ec554', '#6ce387', '#5cbeea',
  '#ec0909', '#2f09ec', '#09ec82', '#a24da8', '#7f1076',
  '#22555e', '#12edd4', '#888888',
];

export default function SubjectEditModal({ mode, subject, onClose, onSaved }) {
  const isEdit = mode === 'edit';

  const [name, setName]         = useState(isEdit ? subject.name : '');
  const [color, setColor]       = useState(isEdit ? subject.color : '#888888');
  const [position, setPosition] = useState(isEdit && subject.position !== null ? String(subject.position) : '');
  const [noJournal, setNoJournal] = useState(isEdit ? !!subject.noJournal : false);
  const [archive, setArchive]   = useState(isEdit ? !!subject.archive : false);

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError('Название обязательно'); return; }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        color,
        position: position === '' ? null : Number(position),
        noJournal,
        archive,
      };
      const res = isEdit
        ? await api.put('/api/subjects/' + subject.id, payload)
        : await api.post('/api/subjects', payload);
      onSaved?.(res.subject);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось сохранить'));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!confirm(`Удалить предмет «${subject.name}»?`)) return;
    setSaving(true); setError(null);
    try {
      await api.delete('/api/subjects/' + subject.id);
      onSaved?.({ id: subject.id, _deleted: true });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось'));
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{isEdit ? `Предмет — ${subject.name}` : 'Новый предмет'}</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>
        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Название *</label>
              <input
                className="sd-edit__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                placeholder="Математика"
              />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Цвет</label>
              <div className="subj-color-row">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="subj-color-pick"
                />
                <input
                  className="sd-edit__input mono"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  style={{ maxWidth: 120 }}
                />
                <div className="subj-color-palette">
                  {PALETTE.map((c) => (
                    <button
                      type="button"
                      key={c}
                      className={'subj-color-swatch' + (c.toLowerCase() === color.toLowerCase() ? ' is-on' : '')}
                      style={{ background: c }}
                      onClick={() => setColor(c)}
                      title={c}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Позиция</label>
              <input
                className="sd-edit__input"
                type="number"
                min="0"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder="без сортировки → в конец"
              />
              <div className="sd-edit__hint">Чем меньше число, тем выше предмет в списках.</div>
            </div>
            <div className="sd-edit__field" />

            <div className="sd-edit__field sd-edit__field--full">
              <label className="staff-auth-row">
                <input type="checkbox" checked={noJournal} onChange={(e) => setNoJournal(e.target.checked)} />
                <span>Не вести дневник по этому предмету</span>
              </label>
              <div className="sd-edit__hint">Например, «Динамическая пауза», «Самоподготовка» — без оценок и записей в журнале.</div>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="staff-auth-row">
                <input type="checkbox" checked={archive} onChange={(e) => setArchive(e.target.checked)} />
                <span>В архиве (не показывать в выборе)</span>
              </label>
            </div>
          </div>
          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            {isEdit && (
              <button type="button" className="btn btn--sm btn--danger" onClick={onDelete} disabled={saving}>Удалить</button>
            )}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (isEdit ? 'Сохранить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
